import { endOfDay, startOfDay, isValid } from "date-fns";
import { makeAutoObservable, runInAction } from "mobx";

import {
  AgentAPI,
  ClientAPI,
  DictionaryAPI,
  FinancialReportAPI,
  HallAPI,
  KioskAPI,
} from "@/api";
import { SearchParamKey } from "@/constants";
import { ViewModel } from "@/hooks/use-view-model";
import ItemsQuery, { TSubmitOptions } from "@/models/items-query";
import { GlobalStore, SelectorStore } from "@/stores";
import { parseInteger } from "@/utilities";

export class FinancialReportState implements ViewModel<unknown> {
  constructor(private globalStore: GlobalStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  onViewMount() {
    this.useInitialSearchParams();
    this.updateSearchParams();
    this.updateSelectorParameters();
    this.apply();
  }

  _tab = "detailed";

  get tab() {
    return this._tab;
  }

  set tab(value: string) {
    this._tab = value;
    this.apply();
  }

  get routerService() {
    return this.globalStore.routerService;
  }

  get userStore() {
    return this.globalStore.userStore;
  }

  get permissionsStore() {
    return this.globalStore.permissionsStore;
  }

  private handleClientSelect = () => {
    this.agentSelectorStore.setSelectedId();
    this.hallSelectorStore.setSelectedId();
    this.playerSelectorStore.setSelectedId();
    this.kioskSelectorStore.setSelectedId();

    this.updateSearchParams();
    this.updateSelectorParameters();
  };

  private handleAgentSelect = () => {
    this.hallSelectorStore.setSelectedId();
    this.playerSelectorStore.setSelectedId();
    this.kioskSelectorStore.setSelectedId();

    this.updateSearchParams();
    this.updateSelectorParameters();
  };

  private handleSubagentSelect = () => {
    this.hallSelectorStore.setSelectedId();
    this.playerSelectorStore.setSelectedId();
    this.kioskSelectorStore.setSelectedId();

    this.updateSearchParams();
    this.updateSelectorParameters();
  };

  private handleHallSelect = () => {
    this.playerSelectorStore.setSelectedId();
    this.kioskSelectorStore.setSelectedId();

    this.updateSearchParams();
    this.updateSelectorParameters();
  };

  private handlePlayerSelect = () => {
    if (this.playerSelectorStore.selectedId) {
      this.kioskSelectorStore.setSelectedId();
    }
    this.updateSearchParams();
    this.updateSelectorParameters();
  };

  private handleKioskSelect = () => {
    if (this.playerSelectorStore.selectedId) {
      this.playerSelectorStore.setSelectedId();
    }
    this.updateSearchParams();
    this.updateSelectorParameters();
  };

  public readonly clientSelectorStore = new SelectorStore({
    filterMethod: ClientAPI.filter,
    getByIdMethod: ClientAPI.getById,
    labelKey: "name",
    onSelect: this.handleClientSelect,
  });

  public readonly agentSelectorStore = new SelectorStore({
    filterMethod: AgentAPI.filter,
    getByIdMethod: AgentAPI.getById,
    labelKey: "name",
    onSelect: this.handleAgentSelect,
  });

  public readonly subagentSelectorStore = new SelectorStore({
    filterMethod: AgentAPI.filter,
    getByIdMethod: AgentAPI.getById,
    labelKey: "name",
    onSelect: this.handleSubagentSelect,
  });

  public readonly hallSelectorStore = new SelectorStore({
    filterMethod: HallAPI.filter,
    getByIdMethod: HallAPI.getById,
    labelKey: "name",
    onSelect: this.handleHallSelect,
  });

  public readonly playerSelectorStore = new SelectorStore({
    filterMethod: DictionaryAPI.getPlayers,
    getByIdMethod: DictionaryAPI.getPlayer,
    renderLabel(item) {
      if (item.isDeleted) {
        return <span style={{ color: "gray" }}>{item.login}</span>;
      }
      return item.login;
    },
    onSelect: this.handlePlayerSelect,
  });

  public readonly kioskSelectorStore = new SelectorStore({
    filterMethod: KioskAPI.filter,
    getByIdMethod: KioskAPI.getById,
    labelKey: "name",
    onSelect: this.handleKioskSelect,
  });

  private _currency?: string = (() => {
    if (this.userStore.hall) {
      return this.userStore.hall.currency;
    } else if (this.userStore.agent) {
      return this.userStore.agent.currency ?? undefined;
    } else if (this.userStore.client) {
      return this.userStore.client.currency ?? undefined;
    }
  })();

  get currency() {
    return this._currency;
  }

  setCurrency = (currency?: string) => {
    this._currency = currency;
    this.updateSearchParams();
  };

  private _dateTimeRange: [Date, Date] = [
    startOfDay(new Date()),
    endOfDay(new Date()),
  ];

  get dateTimeRange() {
    return this._dateTimeRange;
  }

  setDateTimeRange = (dateTimeRange: [Date, Date]) => {
    this._dateTimeRange = dateTimeRange;
    this.updateSearchParams();
  };

  private _isCompactModeEnabled = true;

  get isCompactModeEnabled() {
    return this._isCompactModeEnabled;
  }

  set isCompactModeEnabled(value: boolean) {
    this._isCompactModeEnabled = value;
  }

  get entity() {
    let params:
      | { clientId: number }
      | { agentId: number }
      | { hallId: number }
      | { playerId: number }
      | { kioskId: number }
      | null = null;
    if (this.playerSelectorStore.selectedId) {
      params = { playerId: this.playerSelectorStore.selectedId };
    } else if (this.kioskSelectorStore.selectedId) {
      params = { kioskId: this.kioskSelectorStore.selectedId };
    } else if (this.hallSelectorStore.selectedId) {
      params = { hallId: this.hallSelectorStore.selectedId };
    } else if (this.subagentSelectorStore.selectedId) {
      params = { agentId: this.subagentSelectorStore.selectedId };
    } else if (this.agentSelectorStore.selectedId) {
      params = { agentId: this.agentSelectorStore.selectedId };
    } else if (this.clientSelectorStore.selectedId) {
      params = { clientId: this.clientSelectorStore.selectedId };
    }
    return params;
  }

  filterQuery = new ItemsQuery(FinancialReportAPI.filter, {
    isSearchEnabled: false,
    isOrderEnabled: false,
    isPaginationEnabled: true,
  });

  filter = async (options?: TSubmitOptions) => {
    if (this.entity === null) {
      return;
    }

    await this.filterQuery.submit(
      {
        ...this.entity,
        currency: this.currency,
        dateTimeRange: {
          from: this.dateTimeRange[0].toISOString(),
          to: this.dateTimeRange[1].toISOString(),
        },
      },
      options,
    );
  };

  aggregateByCurrencyQuery = new ItemsQuery(FinancialReportAPI.aggregate, {
    isSearchEnabled: false,
    isOrderEnabled: false,
    isPaginationEnabled: true,
  });

  aggregateByCurrency = async (options?: TSubmitOptions) => {
    if (this.entity === null) {
      return;
    }

    await this.aggregateByCurrencyQuery.submit(
      {
        ...this.entity,
        groupBy: "currency",
        currency: this.currency,
        dateTimeRange: {
          from: this.dateTimeRange[0].toISOString(),
          to: this.dateTimeRange[1].toISOString(),
        },
      },
      options,
    );
  };

  aggregateByPlayerQuery = new ItemsQuery(FinancialReportAPI.aggregate, {
    isSearchEnabled: false,
    isOrderEnabled: false,
    isPaginationEnabled: true,
  });

  aggregateByPlayer = async (options?: TSubmitOptions) => {
    if (this.entity === null) {
      return;
    }

    await this.aggregateByPlayerQuery.submit(
      {
        ...this.entity,
        groupBy: "player",
        currency: this.currency,
        dateTimeRange: {
          from: this.dateTimeRange[0].toISOString(),
          to: this.dateTimeRange[1].toISOString(),
        },
      },
      options,
    );
  };

  aggregateByKioskQuery = new ItemsQuery(FinancialReportAPI.aggregate, {
    isSearchEnabled: false,
    isOrderEnabled: false,
    isPaginationEnabled: true,
  });

  aggregateByKiosk = async (options?: TSubmitOptions) => {
    if (this.entity === null) {
      return;
    }

    await this.aggregateByKioskQuery.submit(
      {
        ...this.entity,
        groupBy: "kiosk",
        currency: this.currency,
        dateTimeRange: {
          from: this.dateTimeRange[0].toISOString(),
          to: this.dateTimeRange[1].toISOString(),
        },
      },
      options,
    );
  };

  aggregateByUserQuery = new ItemsQuery(FinancialReportAPI.aggregate, {
    isSearchEnabled: false,
    isOrderEnabled: false,
    isPaginationEnabled: true,
  });

  aggregateByUser = async (options?: TSubmitOptions) => {
    if (this.entity === null) {
      return;
    }

    await this.aggregateByUserQuery.submit(
      {
        ...this.entity,
        groupBy: "user",
        currency: this.currency,
        dateTimeRange: {
          from: this.dateTimeRange[0].toISOString(),
          to: this.dateTimeRange[1].toISOString(),
        },
      },
      options,
    );
  };

  aggregateByHallQuery = new ItemsQuery(FinancialReportAPI.aggregate, {
    isSearchEnabled: false,
    isOrderEnabled: false,
    isPaginationEnabled: true,
  });

  aggregateByHall = async (options?: TSubmitOptions) => {
    if (this.entity === null) {
      return;
    }

    await this.aggregateByHallQuery.submit(
      {
        ...this.entity,
        groupBy: "hall",
        currency: this.currency,
        dateTimeRange: {
          from: this.dateTimeRange[0].toISOString(),
          to: this.dateTimeRange[1].toISOString(),
        },
      },
      options,
    );
  };

  apply = async () => {
    const method = {
      detailed: this.filter,
      currency: this.aggregateByCurrency,
      halls: this.aggregateByHall,
      players: this.aggregateByPlayer,
      kiosks: this.aggregateByKiosk,
      users: this.aggregateByUser,
    }[this.tab];
    await method?.();
  };

  private useInitialSearchParams = () => {
    const { client, agent, hall } = this.userStore;

    if (client) {
      this.clientSelectorStore.setSelectedId(client.id, false);
    } else if (agent) {
      this.clientSelectorStore.setSelectedId(agent.clientId, false);
      this.agentSelectorStore.setSelectedId(agent.id, false);
    } else if (hall) {
      this.clientSelectorStore.setSelectedId(hall.clientId, false);
      this.agentSelectorStore.setSelectedId(hall.agentId ?? undefined, false);
      this.hallSelectorStore.setSelectedId(hall.id, false);
    }

    const { searchParams } = this.routerService;
    const initialClientId = parseInteger(searchParams[SearchParamKey.ClientId]);
    const initialAgentId =
      initialClientId || this.clientSelectorStore.selectedId
        ? parseInteger(searchParams[SearchParamKey.AgentId])
        : undefined;
    const initialHallId =
      initialClientId || this.clientSelectorStore.selectedId
        ? parseInteger(searchParams[SearchParamKey.HallId])
        : undefined;
    const initialPlayerId =
      initialClientId || this.clientSelectorStore.selectedId
        ? parseInteger(searchParams[SearchParamKey.PlayerId])
        : undefined;
    const initialKioskId =
      initialClientId || this.clientSelectorStore.selectedId
        ? parseInteger(searchParams[SearchParamKey.KioskId])
        : undefined;

    runInAction(() => {
      this._currency = searchParams[SearchParamKey.Currency];

      const df = new Date(searchParams[SearchParamKey.DateFrom]);
      const dt = new Date(searchParams[SearchParamKey.DateTo]);
      if (isValid(df) && isValid(dt) && df < dt) {
        this._dateTimeRange = [df, dt];
      }
    });

    if (this.permissionsStore.has("SelectClient")) {
      this.clientSelectorStore.setSelectedId(initialClientId);
    }
    if (this.clientSelectorStore.selectedId) {
      if (this.permissionsStore.has("SelectAgent")) {
        this.agentSelectorStore.setSelectedId(initialAgentId);
      }
      if (
        this.permissionsStore.has("SelectSubagent") &&
        agent?.id !== initialAgentId
      ) {
        this.subagentSelectorStore.setSelectedId(initialAgentId);
      }
      if (this.permissionsStore.has("SelectHall")) {
        this.hallSelectorStore.setSelectedId(initialHallId);
      }
    }

    this.playerSelectorStore.setSelectedId(initialPlayerId);
    this.kioskSelectorStore.setSelectedId(initialKioskId);
  };

  private updateSearchParams() {
    const urlSearchParams = new URLSearchParams(
      this.routerService.location.search,
    );

    if (this.clientSelectorStore.selectedId) {
      urlSearchParams.set(
        SearchParamKey.ClientId,
        this.clientSelectorStore.selectedId.toString(),
      );
    } else {
      urlSearchParams.delete(SearchParamKey.ClientId);
    }

    if (this.subagentSelectorStore.selectedId) {
      urlSearchParams.set(
        SearchParamKey.AgentId,
        this.subagentSelectorStore.selectedId.toString(),
      );
    } else if (this.agentSelectorStore.selectedId) {
      urlSearchParams.set(
        SearchParamKey.AgentId,
        this.agentSelectorStore.selectedId.toString(),
      );
    } else {
      urlSearchParams.delete(SearchParamKey.AgentId);
    }

    if (this.hallSelectorStore.selectedId) {
      urlSearchParams.set(
        SearchParamKey.HallId,
        this.hallSelectorStore.selectedId.toString(),
      );
    } else {
      urlSearchParams.delete(SearchParamKey.HallId);
    }

    if (this.kioskSelectorStore.selectedId) {
      urlSearchParams.set(
        SearchParamKey.KioskId,
        this.kioskSelectorStore.selectedId.toString(),
      );
    } else {
      urlSearchParams.delete(SearchParamKey.KioskId);
    }

    if (this.playerSelectorStore.selectedId) {
      urlSearchParams.set(
        SearchParamKey.PlayerId,
        this.playerSelectorStore.selectedId.toString(),
      );
    } else {
      urlSearchParams.delete(SearchParamKey.PlayerId);
    }

    if (this.currency) {
      urlSearchParams.set(SearchParamKey.Currency, this.currency);
    } else {
      urlSearchParams.delete(SearchParamKey.Currency);
    }

    urlSearchParams.set(
      SearchParamKey.DateFrom,
      this.dateTimeRange[0].toISOString(),
    );
    urlSearchParams.set(
      SearchParamKey.DateTo,
      this.dateTimeRange[1].toISOString(),
    );

    this.routerService.replace({
      search: urlSearchParams.toString(),
    });
  }

  private updateSelectorParameters = () => {
    const oldClientParameters = this.clientSelectorStore.parameters;
    const oldAgentParameters = this.agentSelectorStore.parameters;
    const oldSubagentParameters = this.subagentSelectorStore.parameters;
    const oldHallParameters = this.hallSelectorStore.parameters;
    const oldKioskParameters = this.kioskSelectorStore.parameters;
    const oldPlayerParameters = this.playerSelectorStore.parameters;

    let newClientParameters = oldClientParameters;
    let newAgentParameters = oldAgentParameters;
    let newSubagentParameters = oldSubagentParameters;
    let newHallParameters = oldHallParameters;
    let newKioskParameters = oldKioskParameters;
    let newPlayerParameters = oldPlayerParameters;

    const clientId = this.clientSelectorStore.selectedId;
    const agentId = this.agentSelectorStore.selectedId;
    const subagentId = this.subagentSelectorStore.selectedId;
    const hallId = this.hallSelectorStore.selectedId;

    const { user } = this.userStore;
    newClientParameters = {
      managerId: user.role === "Manager" ? user.id : undefined,
    };

    if (clientId) {
      newAgentParameters = { clientId };
      newSubagentParameters = { clientId };
      newHallParameters = { clientId };
      newKioskParameters = { clientId };
      newPlayerParameters = { clientId };
    }

    if (agentId) {
      newSubagentParameters = { parentAgentId: agentId };
      newHallParameters = { agentId };
      newKioskParameters = { agentId };
      newPlayerParameters = { agentId };
    }

    if (subagentId) {
      const agentId = subagentId;
      newHallParameters = { agentId };
      newKioskParameters = { agentId };
      newPlayerParameters = { agentId };
    }

    if (hallId) {
      newKioskParameters = { hallId };
      newPlayerParameters = { hallId };
    }

    if (
      this.permissionsStore.has("SelectClient") &&
      JSON.stringify(oldClientParameters) !==
        JSON.stringify(newClientParameters)
    ) {
      this.clientSelectorStore.setParameters(newClientParameters);
      this.clientSelectorStore.fetchItems();
    }

    if (
      this.permissionsStore.has("SelectAgent") &&
      JSON.stringify(oldAgentParameters) !== JSON.stringify(newAgentParameters)
    ) {
      this.agentSelectorStore.setParameters(newAgentParameters);
      this.agentSelectorStore.fetchItems();
    }

    if (
      this.permissionsStore.has("SelectSubagent") &&
      JSON.stringify(oldSubagentParameters) !==
        JSON.stringify(newSubagentParameters)
    ) {
      this.subagentSelectorStore.setParameters(newSubagentParameters);
      this.subagentSelectorStore.fetchItems();
    }

    if (
      this.permissionsStore.has("SelectHall") &&
      JSON.stringify(oldHallParameters) !== JSON.stringify(newHallParameters)
    ) {
      this.hallSelectorStore.setParameters(newHallParameters);
      this.hallSelectorStore.fetchItems();
    }

    if (
      JSON.stringify(oldKioskParameters) !== JSON.stringify(newKioskParameters)
    ) {
      this.kioskSelectorStore.setParameters(newKioskParameters);
      this.kioskSelectorStore.fetchItems();
    }

    if (
      JSON.stringify(oldPlayerParameters) !==
      JSON.stringify(newPlayerParameters)
    ) {
      this.playerSelectorStore.setParameters(newPlayerParameters);
      this.playerSelectorStore.fetchItems();
    }
  };
}
